import type { ReactElement } from 'react';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

import { useModelShowPricing } from './useModelShowPricing.js';
import './ModelShowPricingCard.css';

type PricedRowKey = 'vip' | 'exclusive' | 'party';

function parseIntOrZero(value: string): number {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function ModelShowPricingCard(): ReactElement {
  const { pricing, setPricing, isLoading, isSaving, error, save } = useModelShowPricing();

  if (isLoading) {
    return <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.studio.pricing.loading')}</p>;
  }

  if (!pricing) {
    return <p className="fm-error">{error ?? t('common.studio.pricing.loadError')}</p>;
  }

  const rows: Array<{
    key: PricedRowKey;
    label: string;
    unit: string;
    enabled: boolean;
    amount: number;
    pretip: number;
    allowSpy: boolean;
    spy: number;
    flashSpy: number;
    spyLocked: boolean;
  }> = [
    {
      key: 'vip',
      label: t('common.studio.pricing.rowVip'),
      unit: t('common.studio.pricing.unitCoinsMin'),
      enabled: pricing.vipEnabled !== false,
      amount: pricing.vipCoinsPerMinute,
      pretip: pricing.vipPreTipCoins ?? 0,
      allowSpy: pricing.vipSpyEnabled !== false,
      spy: pricing.vipSpyCoinsPerMinute ?? 0,
      flashSpy: pricing.vipFlashSpyCoins ?? 0,
      spyLocked: false,
    },
    {
      key: 'exclusive',
      label: t('common.studio.pricing.rowExclusive'),
      unit: t('common.studio.pricing.unitCoinsMin'),
      enabled: pricing.exclusiveEnabled !== false,
      amount: pricing.exclusiveCoinsPerMinute,
      pretip: pricing.exclusivePreTipCoins ?? 0,
      allowSpy: false,
      spy: 0,
      flashSpy: 0,
      spyLocked: true,
    },
    {
      key: 'party',
      label: t('common.studio.pricing.rowParty'),
      unit: t('common.studio.pricing.unitCoins'),
      enabled: pricing.partyEnabled !== false,
      amount: pricing.partyEntryCoins,
      pretip: pricing.partyPreTipCoins ?? 0,
      allowSpy: pricing.partySpyEnabled !== false,
      spy: pricing.partySpyCoinsPerMinute ?? 0,
      flashSpy: pricing.partyFlashSpyCoins ?? 0,
      spyLocked: false,
    },
  ];

  const patchRow = (key: PricedRowKey, field: string, value: boolean | number): void => {
    if (key === 'vip') {
      if (field === 'enabled') setPricing({ ...pricing, vipEnabled: Boolean(value) });
      if (field === 'amount') setPricing({ ...pricing, vipCoinsPerMinute: Number(value) });
      if (field === 'pretip') setPricing({ ...pricing, vipPreTipCoins: Number(value) });
      if (field === 'allowSpy') setPricing({ ...pricing, vipSpyEnabled: Boolean(value) });
      if (field === 'spy') setPricing({ ...pricing, vipSpyCoinsPerMinute: Number(value) });
      if (field === 'flashSpy') setPricing({ ...pricing, vipFlashSpyCoins: Number(value) });
    }
    if (key === 'exclusive') {
      if (field === 'enabled') setPricing({ ...pricing, exclusiveEnabled: Boolean(value) });
      if (field === 'amount') setPricing({ ...pricing, exclusiveCoinsPerMinute: Number(value) });
      if (field === 'pretip') setPricing({ ...pricing, exclusivePreTipCoins: Number(value) });
    }
    if (key === 'party') {
      if (field === 'enabled') setPricing({ ...pricing, partyEnabled: Boolean(value) });
      if (field === 'amount') setPricing({ ...pricing, partyEntryCoins: Number(value) });
      if (field === 'pretip') setPricing({ ...pricing, partyPreTipCoins: Number(value) });
      if (field === 'allowSpy') setPricing({ ...pricing, partySpyEnabled: Boolean(value) });
      if (field === 'spy') setPricing({ ...pricing, partySpyCoinsPerMinute: Number(value) });
      if (field === 'flashSpy') setPricing({ ...pricing, partyFlashSpyCoins: Number(value) });
    }
  };

  return (
    <div className="fm-card">
      <h2 style={{ marginTop: 0 }}>{t('common.studio.pricing.title')}</h2>
      <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.studio.pricing.hint')}</p>

      <table className="studio-pricing-table">
        <thead>
          <tr>
            <th>{t('common.studio.pricing.colItem')}</th>
            <th>{t('common.studio.pricing.colEnabled')}</th>
            <th>{t('common.studio.pricing.colAmount')}</th>
            <th>{t('common.studio.pricing.colPretip')}</th>
            <th>{t('common.studio.pricing.colAllowSpy')}</th>
            <th>{t('common.studio.pricing.colSpy')}</th>
            <th>{t('common.studio.pricing.colFlashSpy')}</th>
            <th>{t('common.studio.pricing.colUnit')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const spyInputsOff = row.spyLocked || !row.allowSpy;
            return (
              <tr key={row.key}>
                <td>{row.label}</td>
                <td>
                  <input
                    type="checkbox"
                    checked={row.enabled}
                    onChange={(event) => {
                      patchRow(row.key, 'enabled', event.target.checked);
                    }}
                    aria-label={`${row.label} ${t('common.studio.pricing.colEnabled')}`}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    min={0}
                    value={row.amount}
                    onChange={(event) => {
                      patchRow(row.key, 'amount', parseIntOrZero(event.target.value));
                    }}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    min={0}
                    value={row.pretip}
                    onChange={(event) => {
                      patchRow(row.key, 'pretip', parseIntOrZero(event.target.value));
                    }}
                  />
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={row.allowSpy}
                    disabled={row.spyLocked}
                    title={
                      row.spyLocked ? t('common.studio.pricing.exclusiveSpyLocked') : undefined
                    }
                    onChange={(event) => {
                      patchRow(row.key, 'allowSpy', event.target.checked);
                    }}
                    aria-label={`${row.label} ${t('common.studio.pricing.colAllowSpy')}`}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    min={0}
                    value={row.spy}
                    disabled={spyInputsOff}
                    onChange={(event) => {
                      patchRow(row.key, 'spy', parseIntOrZero(event.target.value));
                    }}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    min={0}
                    value={row.flashSpy}
                    disabled={spyInputsOff}
                    onChange={(event) => {
                      patchRow(row.key, 'flashSpy', parseIntOrZero(event.target.value));
                    }}
                  />
                </td>
                <td>{row.unit}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="studio-pricing-note">{t('common.studio.pricing.tableNote')}</p>

      {error ? <p className="fm-error">{error}</p> : null}
      <div style={{ marginTop: '1rem' }}>
        <Button
          label={isSaving ? t('common.studio.pricing.saving') : t('common.studio.pricing.save')}
          onClick={() => {
            void save({
              vipEnabled: pricing.vipEnabled,
              exclusiveEnabled: pricing.exclusiveEnabled,
              partyEnabled: pricing.partyEnabled,
              vipCoinsPerMinute: pricing.vipCoinsPerMinute,
              exclusiveCoinsPerMinute: pricing.exclusiveCoinsPerMinute,
              partyEntryCoins: pricing.partyEntryCoins,
              vipPreTipCoins: pricing.vipPreTipCoins,
              exclusivePreTipCoins: pricing.exclusivePreTipCoins,
              partyPreTipCoins: pricing.partyPreTipCoins,
              vipSpyEnabled: pricing.vipSpyEnabled,
              partySpyEnabled: pricing.partySpyEnabled,
              vipSpyCoinsPerMinute: pricing.vipSpyCoinsPerMinute,
              partySpyCoinsPerMinute: pricing.partySpyCoinsPerMinute,
              vipFlashSpyCoins: pricing.vipFlashSpyCoins,
              partyFlashSpyCoins: pricing.partyFlashSpyCoins,
              exclusiveSpyEnabled: false,
              exclusiveSpyCoinsPerMinute: 0,
              exclusiveFlashSpyCoins: 0,
            });
          }}
          disabled={isSaving}
        />
      </div>
    </div>
  );
}
